import { useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { isInternalRenderPath } from "./lib/internalRenderBootstrap";

const sentryDsn = process.env.REACT_APP_SENTRY_DSN;

if (!isInternalRenderPath() && sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: process.env.REACT_APP_ENV || "development",

    // Release name must match the value used when uploading source maps
    // (yarn sentry:sourcemaps --release "$REACT_APP_SENTRY_RELEASE"); without it
    // Sentry cannot symbolicate the minified bundle and stack traces stay
    // unreadable (e.g. "Bn.block is not a function").
    release: process.env.REACT_APP_SENTRY_RELEASE,

    sendDefaultPii: true,

    // Drop handled API-client errors that the UI already surfaces to the user as a
    // notification (validation / permission / not-found / conflict). The axios
    // interceptor rethrows these with the original response attached, so a 4xx is
    // an expected outcome — not a crash. Reporting them floods Sentry with noise
    // such as 409 NO_PORTAL_ACCESS (GCP-FRONTEND-G). Genuine server faults (5xx)
    // and network errors (no response) still report.
    beforeSend(event, hint) {
      const status = (
        hint?.originalException as
          | { response?: { status?: number } }
          | undefined
      )?.response?.status;
      if (typeof status === "number" && status >= 400 && status < 500) {
        return null;
      }
      return event;
    },

    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Tracing
    tracesSampleRate: process.env.REACT_APP_ENV === "production" ? 0.2 : 1.0,
    tracePropagationTargets: ["localhost", /^https:\/\/.*\.esalabs\.io/],

    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    enableLogs: true,
  });
}
